// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import { useUser } from '@/app/user-context';
import CoursePicker from '@/features/round-init/CoursePicker';
import { calculateCourseHandicap, parseHandicapIndex } from '@/features/round-init/handicap';
import type { Course } from '@/features/round-init/types';
import { useLogRoundCreation } from '@/features/log-round/useLogRoundCreation';
import type { LogRoundMode, LogRoundTee } from '@/features/log-round/types';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { formatLocalDate, localDateToDate, todayLocalDate } from '@/services/localDate';
import appColors from '@/theme/colors';

const HOLES_OPTIONS = [9, 18] as const;

export default function LogRoundScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { createRound } = useLogRoundCreation();

  const [playerId, setPlayerId] = useState<string | null>(null);
  const [handicapIndex, setHandicapIndex] = useState<number | null>(null);

  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [courseTees, setCourseTees] = useState<LogRoundTee[]>([]);
  const [selectedTeeId, setSelectedTeeId] = useState<string | null>(null);
  const [holesCount, setHolesCount] = useState<number>(18);
  const [playDate, setPlayDate] = useState(() => todayLocalDate());
  const [playDatePickerOpen, setPlayDatePickerOpen] = useState(false);
  const [scp, setScp] = useState('');
  const [scpEditedByHand, setScpEditedByHand] = useState(false);
  const [mode, setMode] = useState<LogRoundMode>('total');

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.token || !user?.id) return;
    const controller = new AbortController();
    fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
      headers: { Authorization: `Bearer ${user.token}` },
      signal: controller.signal,
    })
      .then(async (response) => (response.ok ? ((await response.json()) as { id?: string; handicap?: unknown; Handicap?: unknown }) : null))
      .then((payload) => {
        if (!payload) return;
        if (payload.id) setPlayerId(payload.id);
        const index = parseHandicapIndex(payload.handicap ?? payload.Handicap);
        if (index !== null) setHandicapIndex(index);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [apiBaseUrl, user?.id, user?.token]);

  useEffect(() => {
    if (!user?.token || !selectedCourse?.id) {
      setCourseTees([]);
      setSelectedTeeId(null);
      return;
    }
    const controller = new AbortController();
    fetch(`${apiBaseUrl}/api/geodata/courses/${selectedCourse.id}`, {
      headers: { Authorization: `Bearer ${user.token}` },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as {
          tees?: Array<{ id?: string; name?: string; sex?: string; courseRating?: number; slopeRating?: number; par?: number }>;
          Tees?: Array<{ Id?: string; Name?: string; Sex?: string; CourseRating?: number; SlopeRating?: number; Par?: number }>;
        };
      })
      .then((payload) => {
        if (!payload) {
          setCourseTees([]);
          setSelectedTeeId(null);
          return;
        }
        const teeList = (payload.tees ?? payload.Tees ?? [])
          .map((tee) => ({
            id: ((tee as { id?: string }).id ?? (tee as { Id?: string }).Id ?? '').trim(),
            name: ((tee as { name?: string }).name ?? (tee as { Name?: string }).Name ?? '').trim(),
            sex: ((tee as { sex?: string }).sex ?? (tee as { Sex?: string }).Sex ?? '').trim(),
            courseRating: Number((tee as { courseRating?: number }).courseRating ?? (tee as { CourseRating?: number }).CourseRating ?? 0),
            slopeRating: Number((tee as { slopeRating?: number }).slopeRating ?? (tee as { SlopeRating?: number }).SlopeRating ?? 0),
            par: Number((tee as { par?: number }).par ?? (tee as { Par?: number }).Par ?? 0),
          }))
          .filter((tee) => tee.id.length > 0 && tee.name.length > 0);
        setCourseTees(teeList);
        setSelectedTeeId((prev) => (prev && teeList.some((tee) => tee.id === prev) ? prev : null));
      })
      .catch(() => {
        setCourseTees([]);
        setSelectedTeeId(null);
      });
    return () => controller.abort();
  }, [apiBaseUrl, selectedCourse?.id, user?.token]);

  useEffect(() => {
    if (scpEditedByHand || handicapIndex === null) return;
    const tee = courseTees.find((candidate) => candidate.id === selectedTeeId);
    if (!tee) return;
    const courseHandicap = calculateCourseHandicap({
      handicapIndex,
      slopeRating: tee.slopeRating,
      courseRating: tee.courseRating,
      par: tee.par,
      holesCount,
    });
    if (courseHandicap !== null) {
      setScp(String(courseHandicap));
    }
  }, [courseTees, selectedTeeId, holesCount, handicapIndex, scpEditedByHand]);

  const canSubmit = Boolean(playerId && selectedCourse && scp.trim() && !creating);

  const handleCreate = async () => {
    if (!playerId || !selectedCourse) return;
    setCreating(true);
    setError(null);
    const tee = courseTees.find((candidate) => candidate.id === selectedTeeId) ?? null;
    try {
      const { roundId } = await createRound({
        apiBaseUrl,
        token: user?.token,
        hiddenTourName: t('players.singleRoundHiddenTourName'),
        playerId,
        scp: scp.trim(),
        course: {
          ...selectedCourse,
          selectedTeeId: tee?.id ?? null,
          selectedTeeName: tee?.name ?? null,
        },
        holesCount,
        playDate,
      });
      if (mode === 'total') {
        router.replace(`/round/${roundId}/log-total-score`);
      } else {
        router.replace(`/round/${roundId}/log-scorecard`);
      }
    } catch {
      setError(t('logRound.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <Text style={styles.sectionLabel}>{t('logRound.courseLabel')}</Text>
      <CoursePicker
        selectedCourseId={selectedCourse?.id ?? null}
        selectedCourseName={selectedCourse?.name ?? ''}
        onSelect={(_id, _name, course) => setSelectedCourse(course)}
        token={user?.token}
        playerId={playerId}
      />

      {selectedCourse && courseTees.length > 0 ? (
        <>
          <Text style={styles.sectionLabel}>{t('logRound.teeLabel')}</Text>
          <View style={styles.chipRow}>
            <Pressable
              style={[styles.chip, selectedTeeId === null && styles.chipActive]}
              onPress={() => setSelectedTeeId(null)}
            >
              <Text style={[styles.chipText, selectedTeeId === null && styles.chipTextActive]}>
                {t('players.teeNone')}
              </Text>
            </Pressable>
            {courseTees.map((tee) => (
              <Pressable
                key={tee.id}
                style={[styles.chip, selectedTeeId === tee.id && styles.chipActive]}
                onPress={() => setSelectedTeeId(tee.id)}
              >
                <Text style={[styles.chipText, selectedTeeId === tee.id && styles.chipTextActive]}>{tee.name}</Text>
              </Pressable>
            ))}
          </View>
        </>
      ) : null}

      <Text style={styles.sectionLabel}>{t('logRound.holesLabel')}</Text>
      <View style={styles.chipRow}>
        {HOLES_OPTIONS.map((value) => (
          <Pressable
            key={value}
            style={[styles.chip, holesCount === value && styles.chipActive]}
            onPress={() => setHolesCount(value)}
          >
            <Text style={[styles.chipText, holesCount === value && styles.chipTextActive]}>{value}</Text>
          </Pressable>
        ))}
      </View>

      <Text style={styles.sectionLabel}>{t('logRound.dateLabel')}</Text>
      <Pressable style={styles.dateButton} onPress={() => setPlayDatePickerOpen(true)}>
        <Text style={styles.dateButtonText}>{playDate}</Text>
      </Pressable>
      <PlatformDatePicker
        open={playDatePickerOpen}
        date={localDateToDate(playDate) ?? new Date()}
        onConfirm={(date) => {
          setPlayDate(formatLocalDate(date));
          setPlayDatePickerOpen(false);
        }}
        onCancel={() => setPlayDatePickerOpen(false)}
        closeLabel={t('menu.close')}
      />

      <Text style={styles.sectionLabel}>{t('logRound.handicapLabel')}</Text>
      <TextInput
        style={styles.textInput}
        value={scp}
        onChangeText={(value) => {
          setScp(value);
          setScpEditedByHand(true);
        }}
        keyboardType="numeric"
        placeholder="0"
        placeholderTextColor={appColors.text.muted}
        testID="log-round-hcp"
      />

      <Text style={styles.sectionLabel}>{t('logRound.modeLabel')}</Text>
      <View style={styles.chipRow}>
        <Pressable
          style={[styles.chip, mode === 'total' && styles.chipActive]}
          onPress={() => setMode('total')}
          testID="log-round-mode-total"
        >
          <Text style={[styles.chipText, mode === 'total' && styles.chipTextActive]}>{t('logRound.modeTotal')}</Text>
        </Pressable>
        <Pressable
          style={[styles.chip, mode === 'holeByHole' && styles.chipActive]}
          onPress={() => setMode('holeByHole')}
          testID="log-round-mode-holeByHole"
        >
          <Text style={[styles.chipText, mode === 'holeByHole' && styles.chipTextActive]}>
            {t('logRound.modeHoleByHole')}
          </Text>
        </Pressable>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <GoffyButton
        label={creating ? t('logRound.creating') : t('logRound.createButton')}
        onPress={handleCreate}
        disabled={!canSubmit}
        testID="log-round-submit"
      />
      {creating ? <ActivityIndicator size="small" color={appColors.accent.primary} style={styles.spinner} /> : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 12,
    gap: 8,
    paddingBottom: 32,
  },
  sectionLabel: {
    fontSize: 12.5,
    fontWeight: '700',
    color: appColors.text.muted,
    marginTop: 8,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 13,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  chipActive: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  chipTextActive: {
    color: appColors.text.inverse,
  },
  dateButton: {
    paddingHorizontal: 13,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    alignSelf: 'flex-start',
  },
  dateButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.heading,
  },
  textInput: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 10,
    paddingHorizontal: 13,
    paddingVertical: 10,
    fontSize: 14,
    color: appColors.text.heading,
    maxWidth: 120,
  },
  error: {
    fontSize: 13,
    color: appColors.status.dangerText,
    marginTop: 8,
  },
  spinner: {
    marginTop: 8,
  },
});
